import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaGithub, FaExternalLinkAlt, FaDownload } from 'react-icons/fa';

const ProjectsSection = () => {
  const { t, i18n } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Praca inżynierska - aplikacja dietetyczna KUPiJEDZ',
      description: 'Aplikacja dietetyczna stworzona w ramach pracy inżynierskiej. Pozwala na wyszukiwanie produktów po kodzie kreskowym i nazwie, tworzenie listy zakupów, tworzenie przepisów w oparciu o listę zakupów, dodawanie produktów i przepisów, obliczanie parametrów żywieniowych użytkownika',
      image: 'project1.jpg',
      technologies: ['React', 'Node.js', 'MongoDB'],
      category: 'web',
      github: 'https://github.com/',
      demo: 'https://example.com/',
    },
    {
      id: 2,
      title: 'Aplikacja mobilna do protokołów serwisowych',
      description: 'Aplikacja do zarządzania i tworzenia protokołów serwisowych. Generowanie gotowych dokumentów PDF z zebranym podpisem klienta. Eksportowanie dokumentów do systemów ITSM, Teams, Outlook, pamięć urządzenia. Aplikacja wdrożona na Azure.',
      image: 'project2.jpg',
      technologies: ['React-Native', 'Node.js','Javascript','Azure'],
      category: 'mobile',
      github: 'https://github.com/',
      demo: 'https://example.com/',
    },
    {
      id: 3,
      title: 'Gra szachowa na urządzenie mobilne',
      description: 'Gra w szachy na urządzenia z systemem Android i iOS. Stylizowana podobnie do światowego Chess.com. Wspiera grę multiplayer loklanie oraz SI.',
      image: 'project3.jpg',
      technologies: ['React Native', 'Javascript','Typescript'],
      category: 'mobile',
      github: 'https://github.com/',
      demo: 'https://example.com/',
    },
    {
      id: 4,
      title: 'Aplikacja desktopowa do zbiorczego analizowania raportów',
      description: 'Narzędzie do agregacji i analizy danych z wielu raportów. W formacie plików programu Excel bądź CSV',
      image: 'project4.jpg',
      technologies: ['Electron.js', 'Javascript', 'Typescript'],
      category: 'desktop',
      github: 'https://github.com/',
      demo: 'https://example.com/',
    },
    {
      id: 5,
      title: 'Framework do aplikacji webowych',
      description: `Autorski framework ułatwiający tworzenie aplikacji internetowych. Z wykorzystaniem React.js, Express.js, Node.js, Tailwind CSS, MongoDB i PostgreSQL. Przeznaczony do tworzenia startowych szkieletów aplikacji fullstackowych. <br/><br/> <a href="/demo.html" target="_blank" rel="noopener noreferrer">Zobacz demo</a>`,
      image: 'project5.jpg',
      technologies: ['TypeScript', 'Node.js', 'Express.js','React.js','Tailwind CSS','MongoDB','PostgreSQL','Javascript'],
      category: 'web',
      github: 'https://github.com/',
      demo: '/demo.html',
    }
  ];

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'web', label: 'Web' },
    { id: 'mobile', label: 'Mobile' },
    { id: 'desktop', label: 'Desktop' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <ProjectsContainer id="projects">
      <div className="container">
        <div className="section-title">
          <h2>{t('projects.title')}</h2>
        </div>
        
        <ProjectFilters>
          {categories.map(category => (
            <FilterButton 
              key={category.id}
              isActive={activeFilter === category.id}
              onClick={() => setActiveFilter(category.id)}
            >
              {category.label}
            </FilterButton>
          ))}
        </ProjectFilters>
        
        <ProjectsGrid>
          {filteredProjects.map(project => (
            <ProjectCard key={project.id}>
              <ProjectImageContainer>
                <ProjectImage>
                  {/* Zastąp poniższy div rzeczywistym obrazem, gdy będzie dostępny */}
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    backgroundColor: 'var(--gray-800)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--gray-400)',
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    padding: '1rem'
                  }}>
                    {project.title}
                  </div>
                </ProjectImage>
                <ProjectLinks>
                  {project.github && (
                    <ProjectLink href={project.github} target="_blank" rel="noopener noreferrer">
                      <FaGithub />
                    </ProjectLink>
                  )}
                  {project.demo && (
                    <ProjectLink href={project.demo} target="_blank" rel="noopener noreferrer">
                      <FaExternalLinkAlt />
                    </ProjectLink>
                  )}
                  {project.download && (
                    <ProjectLink href={project.download} target="_blank" rel="noopener noreferrer">
                      <FaDownload />
                    </ProjectLink>
                  )}
                </ProjectLinks>
              </ProjectImageContainer>
              
              <ProjectInfo>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription dangerouslySetInnerHTML={{ __html: project.description }} />
                
                <ProjectTechTitle>{t('projects.technologies')}:</ProjectTechTitle>
                <ProjectTechnologies>
                  {project.technologies.map((tech, index) => (
                    <ProjectTech key={index}>{tech}</ProjectTech>
                  ))}
                </ProjectTechnologies>
              </ProjectInfo>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </div>
    </ProjectsContainer>
  );
};

const ProjectsContainer = styled.section`
  padding: 5rem 0;
  background-color: transparent;
  position: relative;

  .container {
    position: relative;
    z-index: 1;
  }
`;

const ProjectFilters = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const FilterButton = styled.button`
  background-color: ${props => props.isActive ? 'var(--primary-color)' : 'var(--gray-200)'};
  color: ${props => props.isActive ? 'var(--white)' : 'var(--text-color)'};
  border: 1px solid ${props => props.isActive ? 'var(--primary-color)' : 'var(--gray-400)'};
  border-radius: var(--border-radius-md);
  padding: 0.5rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all var(--transition-fast) ease;
  
  &:hover {
    background-color: ${props => props.isActive ? 'var(--secondary-color)' : 'var(--gray-300)'};
    border-color: ${props => props.isActive ? 'var(--secondary-color)' : 'var(--gray-500)'};
    transform: translateY(-2px);
  }
`;

const ProjectsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
`;

const ProjectCard = styled.div`
  background-color: var(--gray-200);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: transform var(--transition-normal) ease, box-shadow var(--transition-normal) ease;
  flex: 0 1 350px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 0 20px rgba(57, 255, 20, 0.3), var(--shadow-lg);
  }
`;

const ProjectImageContainer = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-normal) ease;
  
  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProjectLinks = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity var(--transition-normal) ease;
  
  ${ProjectCard}:hover & {
    opacity: 1;
  }
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: var(--white);
  color: var(--primary-color);
  border-radius: 50%;
  font-size: 1.125rem;
  transition: all var(--transition-fast) ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: var(--white);
    transform: translateY(-3px);
  }
`;

const ProjectInfo = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: var(--text-color);
`;

const ProjectDescription = styled.p`
  color: var(--text-light);
  font-size: 0.9375rem;
  line-height: 1.6;
  flex-grow: 1;

  a {
    color: var(--primary-color);
    font-weight: 700;
    text-decoration: underline;

    &:hover {
      color: var(--secondary-color);
    }
  }
`;

const ProjectTechTitle = styled.h4`
  margin: 1.5rem 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
`;

const ProjectTechnologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ProjectTech = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--primary-color);
  background-color: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius-sm);
`;

export default ProjectsSection;